import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { getAuthToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState(null);

  const refresh = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      // Decode JWT payload to see if this tab is an impersonation session
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload.impersonated_by_email) {
          setImpersonation({ admin_email: payload.impersonated_by_email, expires_at: payload.exp * 1000 });
        } else {
          setImpersonation(null);
        }
      } catch { setImpersonation(null); }
    } catch {
      // Only clear the store this tab is actually using
      sessionStorage.removeItem("nb_token");
      if (!sessionStorage.getItem("nb_token")) localStorage.removeItem("nb_token");
      setUser(null);
      setImpersonation(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Admin 3-email OTP gate (Feb 2026): if OTP is required, do NOT store a token —
    // caller must show OTP prompt and finish via `verifyAdminOtp` below.
    if (data && data.otp_required) {
      return { otp_required: true, challenge_id: data.challenge_id, sent_to_hints: data.sent_to_hints || [], email };
    }
    localStorage.setItem("nb_token", data.token);
    // Feb-19: fresh login → clear the per-session announcement gate so popup shows
    try { sessionStorage.removeItem("nb_announcement_seen_this_session"); } catch (e) { /* ignore */ }
    await refresh();
    return data.user;
  };

  const verifyAdminOtp = async ({ email, otp, challenge_id }) => {
    const { data } = await api.post("/auth/admin-login-verify", { email, otp, challenge_id });
    localStorage.setItem("nb_token", data.token);
    try { sessionStorage.removeItem("nb_announcement_seen_this_session"); } catch (e) { /* ignore */ }
    await refresh();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("nb_token", data.token);
    try { sessionStorage.removeItem("nb_announcement_seen_this_session"); } catch (e) { /* ignore */ }
    await refresh();
    return data;
  };

  const logout = () => {
    // Prefer clearing the tab-scoped session first (impersonation exit),
    // fall back to localStorage clear only when there is no session token.
    if (sessionStorage.getItem("nb_token")) {
      sessionStorage.removeItem("nb_token");
    } else {
      localStorage.removeItem("nb_token");
    }
    // Feb-19: clear the per-session announcement gate so next login re-triggers popup
    try { sessionStorage.removeItem("nb_announcement_seen_this_session"); } catch (e) { /* ignore */ }
    setUser(null);
    setImpersonation(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser, impersonation, verifyAdminOtp }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
